import { Component, effect, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { environment } from "env/environment";

import { PageWrapperComponent } from "../../../components/ui-kit/page-components/page-wrapper/page-wrapper.component";
import { AgentConversationInputsComponent } from "./components/agent-conversation-inputs/agent-conversation-inputs.component";
import { AgentConversationShadowComponent } from "./components/agent-conversation-tools/agent-conversation-shadow.component";
import { PrimaryHeaderComponent } from "../../../components/ui-kit/typography/primary-header/primary-header.component";
import { PrimaryTextComponent } from "app/components/ui-kit/typography/primary-text/primary-text.component";

import { UserService } from "app/services/user.service";
import { ConversationService } from "app/services/conversation.service";
import { filter, distinctUntilChanged, take, firstValueFrom, Subscription } from "rxjs";
import { LocationService } from "app/services/location.service";
import { SpinnerComponent } from "app/components/ui-kit/loaders/spinner/spinner.component";
import { blankDataModelSchemaVersion } from "app/constants/conversation-data-models";
import { FlightOptionService } from "app/services/flight-option-service";
import { AppStateService } from "app/services/app-state.service";
import { AgentVoiceComponent } from "./components/agent-conversation-inputs/components/agent-voice/agent-voice.component";
import { EXACT_MATCH_MESSAGES, NO_RESULTS_MESSAGES } from "app/constants/agent-message-constants";

import { findExactMatches } from "app/utils/flight/flight-matching-utils";
import { AgentResultsComponent } from "./components/agent-results/agent-results.component";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { AgentConversationMinimalComponent } from "./components/agent-conversation-minimal/agent-conversation-minimal.component";

import { animateInputAssist } from "./animations/agent-animations";

type AgentMode = 'agent' | 'results' | 'config-flight-option';

const CACHE_KEY = 'refinedFlightResultsCache';
const TRUSTED_CONTACTS_NOTICE_KEY = 'trustedContactsNoticeClicked';
@Component({
  selector: "app-agent",
  standalone: true,
  imports: [
    FormsModule, CommonModule, PageWrapperComponent, AgentConversationMinimalComponent, AgentConversationShadowComponent,
    AgentConversationInputsComponent, PrimaryHeaderComponent, PrimaryTextComponent, SpinnerComponent, AgentResultsComponent, AgentConversationMinimalComponent
  ],
  templateUrl: "./agent.component.html",
  styleUrls: ["./agent.component.scss"],
})
export class AgentComponent implements OnInit, OnDestroy {
  @ViewChild(AgentVoiceComponent) agentVoice!: AgentVoiceComponent;
  profileLocation: string | null = null;
  isLocationChecked: boolean = false;
  locationError: string | null = null;

  userProfile: any = null;
  currentConversationId: string = null;

  agentMode: string; // default conversation
  isLoadingConversation: boolean = true;
  isDataModelProcessing = false;
  hasInitializedModelEffect: boolean = false;
  isInitialCheck: boolean = true;
  showAssignToAgent: boolean = false;
  hasClickedTrustedContactsNotice: boolean = false;

  private routerSubscription?: Subscription;

  constructor(
    private conversationService: ConversationService,
    private userService: UserService,
    private locationService: LocationService,
    private flightService: FlightOptionService,
    private appState: AppStateService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    const initialMode = this.route.snapshot.queryParamMap.get('m');
    if (this.isAgentMode(initialMode)) {
      this.agentMode = initialMode;
      this.appState.agent.mode.set(initialMode);
      this.syncResultsBackButton(initialMode);
    } else {
      this.agentMode = this.appState.agent.mode();
      this.syncResultsBackButton(this.agentMode);
    }

    effect(() => {
      const model = this.appState.agent.currentDataModel();
      if (!this.hasInitializedModelEffect) {
        this.hasInitializedModelEffect = true;
        return;
      }

      if (model && !this.isDataModelProcessing) {
        this.isDataModelProcessing = true;
        queueMicrotask(() => this.onCurrentDataModelChange());
      }
    });
    effect(() => {
      const mode = this.appState.agent.mode();
      if (mode) {
        queueMicrotask(() => {
          this.handleModeChange();
        });
      }
    });
    effect(() => {
      const assisting = this.appState.agent.showInputAssist();
      if (assisting) {
        queueMicrotask(() => {
          animateInputAssist();
        });
      }
    });
  }

  ngOnInit(): void {
    this.hasClickedTrustedContactsNotice = localStorage.getItem(TRUSTED_CONTACTS_NOTICE_KEY) === 'true';
    this.isLoadingConversation = true;
    this.userService.userProfile$
      .pipe(
        filter(profile => !!profile),
        distinctUntilChanged(
          (prev, curr) =>
            prev.currentConversationId === curr.currentConversationId &&
            prev.currentConversationUpdatedAt === curr.currentConversationUpdatedAt
        ),
        take(1)
      )
      .subscribe(profile => {
        this.userProfile = profile;
        this.profileLocation = profile.currentLocation || null;
        this.currentConversationId = profile?.currentConversationId || null;
        this.loadConversation(
          profile.currentConversationId || null,
          profile.currentConversationUpdatedAt || null
        );
        if (!this.isLocationChecked) {
          this.isLocationChecked = true;
          this.getUserLocation();
        }
      });

    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const mode = this.route.snapshot.queryParamMap.get('m');

        if (this.isAgentMode(mode) && mode !== this.agentMode) {
          this.agentMode = mode;
          this.appState.agent.mode.set(mode);
          this.syncResultsBackButton(mode);
        }
      });
  }

  isAgentMode(mode: any): mode is AgentMode {
    return ['agent', 'results'].includes(mode);
  }

  async loadConversation(currentConversationId: string | null, updatedAt: any | null) {

    const updatedAtMillis = updatedAt?.toMillis ? updatedAt.toMillis() : (updatedAt instanceof Date ? updatedAt.getTime() : null);

    if (updatedAtMillis === null) {
      this.handleCreateNewConversation();
      this.isLoadingConversation = false;
      return;
    }

    const now = Date.now();
    const fortyEightHours = 48 * 60 * 60 * 1000; // 48 hours
    const dateDiff = now - updatedAtMillis;

    if (dateDiff > fortyEightHours) {
      this.handleCreateNewConversation();
    } else {
      try {
        const currentConversation = await this.conversationService.getConversation(currentConversationId);
        if (this.isVersionOlder(currentConversation.dataModelSchemaVer, blankDataModelSchemaVersion)) {
          this.handleCreateNewConversation();
        } else if (currentConversation.status === 'inactive') {
          this.handleCreateNewConversation();
        } else {
          this.appState.agent.currentConversation.set(currentConversation.thread);
          this.appState.agent.currentDataModel.set(currentConversation.dataModel);
          this.appState.agent.currentIntent.set(currentConversation.intent);
          this.conversationService.setCurrentConversationId(currentConversationId);
        }
      } catch (err) {
        console.error('Error fetching current conversation:', err);
      }
    }
    this.isLoadingConversation = false;
  }

  async handleCreateNewConversation() {
    this.isLoadingConversation = true;
    try {
      const newConversation = await this.conversationService.createConversation();
      this.appState.resetAgentState();
      this.isLoadingConversation = false;
      this.appState.agent.currentConversation.set(newConversation.thread);
      this.appState.agent.currentDataModel.set(newConversation.dataModel);
      this.appState.agent.currentIntent.set(newConversation.intent);
      this.conversationService.setCurrentConversationId(newConversation.id);
    } catch (err) {
      console.error('Error creating conversation:', err);
      this.isLoadingConversation = false;
    }
  }

  async getUserLocation() {
    try {
      const { latitude, longitude } = await this.locationService.getCurrentPosition();
      const cachedLat = this.userProfile?.currentLat;
      const cachedLng = this.userProfile?.currentLong;

      // If there's no cached location, fetch new location details.
      if (cachedLat == null || cachedLng == null) {
        const locationInfo = await this.locationService.reverseGeocode(latitude, longitude);
        if (locationInfo) {
          this.profileLocation = locationInfo.location;
          await this.userService.updateUserProfile({
            currentLocation: this.profileLocation,
            currentLat: latitude,
            currentLong: longitude,
          });
        }
      }
      // If the current location is not in the same general area, update it.
      else if (!this.locationService.isSameGeneralArea(latitude, longitude, cachedLat, cachedLng)) {
        const locationInfo = await this.locationService.reverseGeocode(latitude, longitude);
        if (locationInfo) {
          this.profileLocation = locationInfo.location;
          await this.userService.updateUserProfile({
            currentLocation: this.profileLocation,
            currentLat: latitude,
            currentLong: longitude,
          });
        }
      }
    } catch (error) {
      console.error('Error getting user location:', error);
      this.locationError = error.message || 'An error occurred while fetching your location.';
    }
  }

  isVersionOlder(versionA: string, versionB: string): boolean {
    const splitA = versionA.split('.').map(Number);
    const splitB = versionB.split('.').map(Number);

    for (let i = 0; i < 3; i++) {
      if ((splitA[i] || 0) < (splitB[i] || 0)) return true;
      if ((splitA[i] || 0) > (splitB[i] || 0)) return false;
    }
    return false; // equal versions
  }

  generateExactMatchResponse(exactMatchesLength: number): string {
    const randomMessageObj = EXACT_MATCH_MESSAGES[Math.floor(Math.random() * EXACT_MATCH_MESSAGES.length)];
    const message = exactMatchesLength === 1 ? randomMessageObj.singular : randomMessageObj.plural;
    return message.replace('{count}', exactMatchesLength.toString());
  }

  async onCurrentDataModelChange() {
    const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    const currentConversation = this.appState.agent.currentConversation();
    const conversationId = this.currentConversationId;
    const conversationLength = currentConversation.length;
    const oneHourAgo = Date.now() - 3600000;

    if (
      cache &&
      cache.conversationId === conversationId &&
      cache.conversationLength === conversationLength &&
      cache.resultTimestamp > oneHourAgo
    ) {
      this.appState.agent.refinedFlightOptions.set(cache.results);
      if (cache.results.optimalMatches.length > 0 || cache.results.similarMatches.length > 0) {
        this.appState.agent.hideInput.set(true);
      }
      this.appState.agent.isSearchingFlights.set(false);
      this.isDataModelProcessing = false;
      this.isInitialCheck = false;
      return;
    }

    this.appState.agent.refinedFlightOptions.set([]);
    this.appState.agent.isNoResult.set(false);
    const currentDataModel = this.appState.agent.currentDataModel();
    const isValid = await this.flightService.isFlightDataModelValid(currentDataModel);

    if (!isValid) {
      this.appState.agent.isSearchingFlights.set(false);
      this.isDataModelProcessing = false;
      this.isInitialCheck = false;
      return;
    }

    this.appState.agent.isSearchingFlights.set(true);
    this.appState.agent.hideInput.set(true);

    try {
      const allFlightOptions = await this.handleFlightQuery(currentDataModel);

      const filteredOptions = allFlightOptions.filter((option) => option.creatorId && option.creatorId !== this.userProfile.id);

      if (filteredOptions?.length === 0) {
        const dynamicResponse = NO_RESULTS_MESSAGES[Math.floor(Math.random() * NO_RESULTS_MESSAGES.length)];
        if (this.appState.agent.ioMethod() === 'voice' && !this.isInitialCheck) {
          this.handleAgentResponseTTS(dynamicResponse);
        }
        this.appState.agent.isNoResult.set(true);
        this.appState.agent.currentConversation.update(convo => {
          const updated = [...convo];
          const last = { ...updated[updated.length - 1], agent: dynamicResponse };
          updated[updated.length - 1] = last;
          this.isInitialCheck = false;
          return updated;
        });
        return;
      };

      const matchData = findExactMatches(currentDataModel, filteredOptions);

      const { exactMatches, otherOptions } = matchData;

      if (otherOptions.length > 0) {
        await this.handleRefineResultsWithLLM(exactMatches, otherOptions);
      } else {
        const matches = {
          optimalMatches: exactMatches,
          similarMatches: [],
        }
        const dynamicResponse = this.generateExactMatchResponse(exactMatches.length);
        if (this.appState.agent.ioMethod() === 'voice' && !this.isInitialCheck) {
          this.handleAgentResponseTTS(dynamicResponse);
        }
        this.appState.agent.refinedFlightOptions.set(matches);
        this.saveResultsToCache(matches);
        this.appState.agent.currentConversation.update(convo => {
          const updated = [...convo];
          const last = { ...updated[updated.length - 1], agent: dynamicResponse };
          updated[updated.length - 1] = last;
          return updated;
        });
        const intent = this.appState.agent.currentIntent();
        const convo = this.appState.agent.currentConversation();
        this.conversationService.updateCurrentConversationData(convo, currentDataModel, intent); // TODO: Redundant code.  Break other uses into a share helper;
      }

    } catch (err) {
      console.error('Flight query error:', err);
    } finally {
      this.appState.agent.isSearchingFlights.set(false);
      this.isDataModelProcessing = false;
      this.isInitialCheck = false;
    }
  }

  async handleFlightQuery(dataModel: any): Promise<any[]> {
    return firstValueFrom(
      this.flightService.getMatchingFlightOptions(dataModel, 7)
    );
  }

  async handleRefineResultsWithLLM(exactMatches, otherOptions) {
    try {
      const dataModel = this.appState.agent.currentDataModel();
      const exactMatchCount = exactMatches.length || 0;

      const llmResponse = await this.flightService.refineFlightOptionsLLM(dataModel, otherOptions, exactMatchCount);
      if (llmResponse.agentResponse) {
        if (this.appState.agent.ioMethod() === 'voice' && !this.isInitialCheck) {
          this.handleAgentResponseTTS(llmResponse.agentResponse);
        }
        this.appState.agent.currentConversation.update(convo => {
          const updated = [...convo];
          const last = { ...updated[updated.length - 1], agent: llmResponse.agentResponse };
          updated[updated.length - 1] = last;
          return updated;
        });
      }
      const intent = this.appState.agent.currentIntent();
      const convo = this.appState.agent.currentConversation();
      this.conversationService.updateCurrentConversationData(convo, dataModel, intent);

      const displayOptions = this.flightService.buildFlightDisplayOptions(exactMatches, otherOptions, llmResponse);
      this.appState.agent.refinedFlightOptions.set(displayOptions);
      this.saveResultsToCache(displayOptions);
    } catch (err) {
      console.error('Error refining options with LLM:', err);
    }
  }

  private saveResultsToCache(results: any) {
    const currentConversation = this.appState.agent.currentConversation();
    const conversationId = this.currentConversationId;
    const conversationLength = currentConversation.length;

    const cacheData = {
      conversationId,
      conversationLength,
      resultTimestamp: Date.now(),
      results
    };

    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  }

  async handleAgentResponseTTS(text: string) {
    const payload = { agentText: text };
    this.appState.agent.isHandlingQuery.set(false);
    this.appState.agent.isSearchingFlights.set(false);
    const response = await fetch(`https://${environment.apiBaseURL}.cloudfunctions.net/getAiTextToSpeech`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    this.appState.agent.singleTTS.set(response);
  }

  handleModeChange() {
    const mode = this.appState.agent.mode();
    const validModes = ['agent', 'results',];

    if (validModes.includes(mode)) {
      this.agentMode = mode;
      this.syncResultsBackButton(mode);

      const url = new URL(window.location.href);
      url.searchParams.set('m', mode);
      window.history.pushState({}, '', url.toString());
    }
  }

  private syncResultsBackButton(mode: string) {
    if (mode === 'results') {
      this.appState.navigation.backButtonState.set({
        action: 'route',
        value: '/agent?m=agent'
      });
      return;
    }

    if (this.appState.navigation.backButtonState()?.value === '/agent?m=agent') {
      this.appState.navigation.backButtonState.set(null);
    }
  }

  openTrustedContactsNotice(): void {
    localStorage.setItem(TRUSTED_CONTACTS_NOTICE_KEY, 'true');
    this.hasClickedTrustedContactsNotice = true;
    this.router.navigate(['/trusted-contacts']);
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }
}
